// Cart panel — the right-hand column of the POS screen.
//
// Renders the current cart line items with qty steppers, plus a footer for
// discount, tax toggle, and grand total. Pure presentation: pricing math is
// driven by lib/pricing + cart store; this component just wires inputs to
// the parent POS route which owns the discount/tax/fulfillment state.

import { useEffect } from 'react';
import {
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  Receipt,
  Inbox,
  Save,
} from 'lucide-react';
import {
  calculateTotals,
  type PricingCartItem,
} from '@/lib/pricing';
import { cn, formatIDR, formatNumber } from '@/lib/format';
import { useCart, isAssigned, type CartItem } from '@/store/cart';
import { formatDate } from '@/lib/format';
import { Toggle } from '@/components/ui/Toggle';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { DiscountToggle } from './DiscountToggle';
import { FulfillmentToggle } from './FulfillmentToggle';
import type {
  DiscountType,
  FulfillmentType,
  PaymentStatus,
} from '@/types/domain';

export interface CartProps {
  // Controlled discount state
  discountType: DiscountType;
  discountValue: number;
  onDiscountChange: (next: { type: DiscountType; value: number }) => void;

  // Controlled tax state
  taxEnabled: boolean;
  taxRate: number;
  onTaxEnabledChange: (next: boolean) => void;

  // Controlled fulfillment state
  fulfillment: FulfillmentType;
  onFulfillmentChange: (next: FulfillmentType) => void;

  // Notify parent of totals / fulfilment for the Checkout summary.
  onTotalsChange?: (totals: {
    subtotal: number;
    discountAmount: number;
    taxAmount: number;
    grandTotal: number;
  }) => void;

  /** Fired when cashier clicks the bottom "Charge" button. */
  onCharge?: () => void;

  /**
   * Fired when cashier clicks "Save as draft" — they have a cart they
   * want to park so they can serve another customer. The parent (POS
   * route) does the actual save.
   */
  onSaveDraft?: () => void;
  savingDraft?: boolean;

  /** Number of drafts the cashier has — shown as a badge on the
   *  "Drafts" button. Parent owns the query so the badge stays in sync
   *  after saves/deletes via React Query invalidation. */
  draftCount?: number;
  onOpenDrafts?: () => void;

  /** Disable the charge button (e.g. event missing). */
  canCharge?: boolean;
  /** Disable the entire cart (e.g. transaction in flight). */
  disabled?: boolean;
  /** Text of the main button; defaults to "Charge <total>". */
  chargeLabel?: (grandTotal: number) => string;
  /** Disable the fulfilment toggle (e.g. read-only view later). */
  hideFulfillment?: boolean;
  /** Status banner — used to show "Pending delivery" etc. */
  paymentStatus?: PaymentStatus;
  className?: string;
}

const toPricing = (items: CartItem[]): PricingCartItem[] =>
  items.map((i) => ({
    productId: i.productId,
    name: i.name,
    price: i.price,
    qty: i.qty,
  }));

export function Cart({
  discountType,
  discountValue,
  onDiscountChange,
  taxEnabled,
  taxRate,
  onTaxEnabledChange,
  fulfillment,
  onFulfillmentChange,
  onTotalsChange,
  onCharge,
  onSaveDraft,
  savingDraft = false,
  draftCount = 0,
  onOpenDrafts,
  canCharge = true,
  disabled = false,
  chargeLabel,
  hideFulfillment = false,
  paymentStatus,
  className,
}: CartProps) {
  const items = useCart((s) => s.items);
  const updateQty = useCart((s) => s.updateQty);
  const removeItem = useCart((s) => s.removeItem);
  const clear = useCart((s) => s.clear);

  const totals = calculateTotals({
    items: toPricing(items),
    discountType,
    discountValue,
    taxEnabled,
    taxRate,
  });

  useEffect(() => {
    onTotalsChange?.(totals);
  }, [totals, onTotalsChange]);

  const itemCount = items.reduce((s, i) => s + i.qty, 0);
  const isEmpty = items.length === 0;

  return (
    <div
      className={cn(
        'flex flex-col h-full bg-surface border border-line rounded-lg overflow-hidden',
        className,
      )}
    >
      <header className="px-4 py-3 border-b border-line flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <ShoppingCart size={14} />
          </span>
          <div className="min-w-0">
            <p className="text-subhead font-display font-semibold text-ink leading-none">
              Keranjang
            </p>
            <p className="text-caption text-ink-subtle mt-1">
              {itemCount} item
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {onOpenDrafts && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onOpenDrafts}
              aria-label="Buka daftar draft"
              title="Draft yang disimpan"
              className="relative"
            >
              <Inbox size={14} />
              <span className="hidden sm:inline">Draft</span>
              {draftCount > 0 && (
                <span
                  className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-semibold leading-none"
                  aria-label={`${draftCount} draft`}
                >
                  {draftCount > 99 ? '99+' : draftCount}
                </span>
              )}
            </Button>
          )}
          {!isEmpty && !disabled && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onDiscountChange({ type: null, value: 0 });
                clear();
              }}
              title="Kosongkan keranjang"
              aria-label="Clear cart"
            >
              <Trash2 size={14} />
              <span className="hidden sm:inline">Clear</span>
            </Button>
          )}
        </div>
      </header>

      {paymentStatus && paymentStatus !== 'paid' && (
        <div
          className={cn(
            'px-4 py-2 text-caption font-medium border-b border-line',
            paymentStatus === 'pending_delivery'
              ? 'bg-warning/10 text-warning'
              : 'bg-danger/10 text-danger',
          )}
        >
          {paymentStatus === 'pending_delivery'
            ? 'Pesanan ini berstatus pending delivery — stock belum dipotong.'
            : `Status: ${paymentStatus}`}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto">
        {isEmpty ? (
          <EmptyState
            icon={<ShoppingCart size={24} strokeWidth={1.5} />}
            title="Keranjang kosong"
            description="Klik produk di sebelah kiri untuk mulai."
            className="py-10"
          />
        ) : (
          <ul role="list" className="divide-y divide-line">
            {items.map((item) => (
              <li key={item.lineId} className="px-4 py-3 flex gap-3">
                <div className="flex-1 min-w-0">
                  <p
                    className="text-small font-medium text-ink truncate"
                    title={item.name}
                  >
                    {item.name}
                  </p>
                  <p className="text-caption text-ink-subtle">
                    {formatIDR(item.price)} × {formatNumber(item.qty)}
                    {item.unit ? ` ${item.unit}` : ''}
                  </p>
                  <LotSummary item={item} />
                  <p className="text-small font-semibold text-ink mt-1 tabular-nums">
                    {formatIDR(item.price * item.qty)}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  <div className="inline-flex items-center border border-line rounded">
                    <button
                      type="button"
                      onClick={() => updateQty(item.lineId, item.qty - 1)}
                      disabled={disabled}
                      className="w-9 h-9 inline-flex items-center justify-center text-ink-subtle hover:text-ink hover:bg-line/30 disabled:opacity-50"
                      aria-label="Kurangi qty"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="w-9 text-center text-small font-medium tabular-nums">
                      {item.qty}
                    </span>
                    <button
                      type="button"
                      onClick={() => updateQty(item.lineId, item.qty + 1)}
                      disabled={disabled || item.qty >= item.maxStock}
                      className="w-9 h-9 inline-flex items-center justify-center text-ink-subtle hover:text-ink hover:bg-line/30 disabled:opacity-50"
                      aria-label="Tambah qty"
                      title={
                        item.qty >= item.maxStock
                          ? `Stok maksimal ${item.maxStock}`
                          : undefined
                      }
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(item.lineId)}
                    disabled={disabled}
                    className="text-caption text-danger hover:underline inline-flex items-center gap-1 py-1.5 px-1"
                    aria-label="Hapus dari keranjang"
                  >
                    <Trash2 size={11} />
                    Hapus
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Compact by design: this footer and the item list share one fixed
          column, so every line here is an item the cashier cannot see. */}
      <footer className="border-t border-line bg-canvas/50 px-4 py-3 space-y-2.5 shrink-0">
        {!isEmpty && !hideFulfillment && (
          <div data-tour="pos-fulfillment">
          <FulfillmentToggle
            value={fulfillment}
            onChange={onFulfillmentChange}
            disabled={disabled}
          />
          </div>
        )}

        {!isEmpty && (
          <DiscountToggle
            type={discountType}
            value={discountValue}
            subtotal={totals.subtotal}
            onChange={onDiscountChange}
            onClear={() => onDiscountChange({ type: null, value: 0 })}
            disabled={disabled}
          />
        )}

        {!isEmpty && (
          <div className="flex items-center justify-between gap-3">
            <p
              className="text-small font-medium text-ink"
              title="Toggle off untuk customer yang tidak kena PPN."
            >
              PPN {taxRate}%
            </p>
            <Toggle
              checked={taxEnabled}
              onChange={onTaxEnabledChange}
              disabled={disabled}
              aria-label="Toggle PPN"
            />
          </div>
        )}

        {!isEmpty && (
          <div className="space-y-0.5 pt-2 border-t border-line">
            <Row label="Subtotal" value={formatIDR(totals.subtotal)} />
            {totals.discountAmount > 0 && (
              <Row
                label={
                  discountType === 'percent'
                    ? `Diskon (${discountValue}%)`
                    : 'Diskon (nominal)'
                }
                value={`- ${formatIDR(totals.discountAmount)}`}
                tone="success"
              />
            )}
            {taxEnabled && (
              <Row
                label={`PPN ${taxRate}%`}
                value={formatIDR(totals.taxAmount)}
              />
            )}
            <Row
              label="Grand total"
              value={formatIDR(totals.grandTotal)}
              bold
            />
          </div>
        )}

        <div data-tour="pos-charge" className="flex items-stretch gap-2">
          <Button
            variant="primary"
            size="lg"
            className="flex-1"
            onClick={onCharge}
            disabled={isEmpty || !canCharge || disabled}
            leftIcon={<Receipt size={16} />}
          >
            {isEmpty
              ? 'Pilih produk dulu'
              : chargeLabel
                ? chargeLabel(totals.grandTotal)
                : `Charge ${formatIDR(totals.grandTotal)}`}
          </Button>
          {!isEmpty && onSaveDraft && (
            <Button
              variant="secondary"
              size="lg"
              onClick={onSaveDraft}
              loading={savingDraft}
              disabled={disabled}
              title="Simpan draft"
              aria-label="Simpan draft"
              className="shrink-0 px-3"
            >
              <Save size={16} />
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}

/**
 * Which batch the units come from. One batch: named here. Several: the split
 * is made on the review step, and this says whether it has been.
 */
function LotSummary({ item }: { item: CartItem }) {
  if (item.lotOptions.length === 0) return null;
  const byId = new Map(item.lotOptions.map((o) => [o.allocationId, o]));
  const named = item.lots
    .map((l) => ({ ...l, opt: byId.get(l.allocationId) }))
    .filter((l) => l.opt && (l.opt.lotNumber || l.opt.expiryDate));
  if (item.lotOptions.length > 1 && !isAssigned(item)) {
    return (
      <p className="text-caption text-warning">
        {item.lotOptions.length} lot tersedia — pilih di langkah berikut
      </p>
    );
  }
  if (named.length === 0) return null;
  return (
    <p className="text-caption text-ink-subtle flex flex-wrap gap-x-2">
      {named.map((l) => (
        <span key={l.allocationId}>
          {item.lots.length > 1 && <span className="tabular-nums">{l.qty}× </span>}
          {l.opt!.lotNumber && (
            <>
              Lot <span className="font-mono text-ink">{l.opt!.lotNumber}</span>
            </>
          )}
          {l.opt!.expiryDate && (
            <span className={l.opt!.expiryDate < Date.now() ? 'text-danger font-medium ml-1' : 'ml-1'}>
              ED {formatDate(l.opt!.expiryDate)}
            </span>
          )}
        </span>
      ))}
    </p>
  );
}

function Row({
  label,
  value,
  tone = 'default',
  bold = false,
}: {
  label: string;
  value: string;
  tone?: 'default' | 'success';
  bold?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2 text-body">
      <span
        className={cn(
          tone === 'success' ? 'text-success' : 'text-ink-subtle',
          bold && 'text-ink font-semibold',
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          'tabular-nums',
          tone === 'success' ? 'text-success' : 'text-ink',
          bold && 'text-subhead font-display font-semibold',
        )}
      >
        {value}
      </span>
    </div>
  );
}
